/**
 * @author: subhasish.ghosh@zoom.us
 */

export const SIDEBAR_ITEMS = Object.freeze({
  summary: "Summary",
  nonPaymentDirect: "Collections Failure (Direct)",
  nonPaymentIndirect: "Collections Failure (Indirect)",
  fraud: "Fraud",
  tns: "Trust & Safety Violation",
  chargeback: "Customer Disputes",
  bizOperations: "Billing Ops Cancel",
  bizOperationIndirect: "Billing Ops Cancel (Indirect)",
  nonRenewalDirect: "Non-Renewal Cancel (Direct)",
  nonRenewalIndirect: "Non-Renewal Cancel (Indirect)",
  productCatalogue: "Product Catalogue",
  admin: "Admin",
});

export const SCREEN_RESOLUTIONS = Object.freeze([
  { minWidth: 3840, value: 3840 }, // 4K Ultra HD
  { minWidth: 2560, value: 2560 }, // QHD (Quad HD)
  { minWidth: 1920, value: 1920 }, // Full HD (1080p)
  { minWidth: 1680, value: 1680 }, // HD+ (large monitors)
  { minWidth: 1440, value: 1440 }, // WQHD
  { minWidth: 1366, value: 1366 }, // WXGA (laptops)
  { minWidth: 1280, value: 1280 }, // HD (720p)
  { minWidth: 1024, value: 1024 }, // XGA (tablets in landscape)
  { minWidth: 960, value: 960 }, // Some tablet devices
  { minWidth: 768, value: 768 }, // Tablets and large phones in portrait
  { minWidth: 640, value: 640 }, // Older phones in landscape
  { minWidth: 480, value: 480 }, // Small phones in landscape
  { minWidth: 320, value: 320 }, // Small phones in portrait
]);

export const DISABLE_DECLINE_CANCELLATION_ACTION_TYPES = [
  "nonRenewalDirect",
  "nonRenewalIndirect",
];

export const CANCELLATION_REASONS = Object.freeze({
  nonPaymentDirect: {
    further_investigation_needed: "Further Investigation Needed",
    path_to_resolution_identified: "Path to Resolution Identified",
    potentially_incorrect_information: "Potentially Incorrect Information",
    promise_to_pay_received: "Promise to Pay Received",
  },
  bizOperations: {
    billing_incorrect_action: "Billing incorrect action",
    renewals_incorrect_request: "Renewals incorrect request",
    ae_incorrect_request: "AE incorrect request",
    customer_incorrect_request: "Customer Incorrect Request",
  },
});

/**
 * Hover-over (tooltip) descriptions for the TNS "Cancellation Reason" (reasonCode) values.
 * Keys are normalised (lower-cased, non-alphanumerics collapsed to "_") so that both the
 * display form ("Fraudulent Account") and the raw code form ("tos_violation") resolve.
 * Use getTnsReasonDescription() in utils to look values up.
 */
export const TNS_REASON_CODE_DESCRIPTIONS = Object.freeze({
  fraudulent_account:
    "Accounts engaged in fraudulent activity. This could be in sign-up, using Zoom to defraud others, or using fraudulent payment methods.",
  fraud_unauthorized_reseller:
    "Accounts selling Zoom products/services without authorization from Zoom.",
  audio_fraud:
    "Fraudulent accounts that engaged in fraudulent activity using Zoom Phone or in-meeting callout feature.",
  ato_fraud_account:
    "The original owner of the account has lost control and a bad actor is now engaging in fraudulent activity on the account.",
  tos_violation:
    "Account engaged in activity that violates Zoom Terms of Service excluding fraud.",
  anti_fraud_billing_suspension:
    "Accounts involved in fraudulent activities identified by anti-fraud team, it could be scam/spam, account setup, or other activity that violates Zoom AUP, etc."
});

export const APPROVER_LEVELS = Object.freeze({
  level_one: "Level 1",
  level_two: "Level 2",
});

export const MONTHS = [
  "",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const TAB = "tab";
export const ACTION_TYPE = "actionType";
export const SEARCH_PARAM_FILENAME = "file";
export const SEARCH_PARAM_PAGE = "page";
export const SEARCH_PARAM_PAGE_DOWNLOAD = "download";
export const SEARCH_PARAM_PAGE_PRODUCT_CATALOG = "productcatalog";
export const SEARCH_PARAM_PATH = "path";

export const SEARCH_PARAM_DEFAULTS_HOME = [
  ACTION_TYPE.toLowerCase(),
  TAB.toLowerCase(),
];
export const SEARCH_PARAM_DEFAULTS_DOWNLOAD = [
  SEARCH_PARAM_PAGE.toLowerCase(),
  SEARCH_PARAM_FILENAME.toLowerCase(),
  SEARCH_PARAM_PATH.toLowerCase(),
];
export const SEARCH_PARAM_DEFAULTS_PRODUCT_CATALOG = [
  SEARCH_PARAM_PAGE.toLowerCase(),
  TAB.toLowerCase(),
];

export const UNCATEGORIZED = "Uncategorized";

// Zuora's ApproverComments__c field rejects values > 255 chars; cap approver comments at 250.
export const MAX_APPROVER_COMMENT_LENGTH = 250;

export const NON_DECIMAL_CURRENCY_CODES = ["JPY", "KRW", "VND"];

export const RESOLUTION_DATE_CASES = Object.freeze({
  apply_to_none: "APPLY_TO_NONE",
  apply_to_missing: "APPLY_TO_MISSING",
  apply_to_all: "APPLY_TO_ALL",
});

export const ZOOM_LOGO =
  "https://st1.zoom.us/fe-static/fe-signup-login-active/img/ZoomNewLogo.b2fd5c95.png";

export const TABLE_TYPES = Object.freeze({
  pending: "Pending Action",
  historical: "Historical Records",
});

export const TABLE_TYPES_KEYS = Object.keys(TABLE_TYPES);

export const TABLE_TYPES_ENTRIES = Object.entries(TABLE_TYPES);

export const DEFAULT_SORT_ORDER = {
  pending: {
    field: "executionDate",
    sort: "asc",
  },
  historical: {
    field: "l2Status.date",
    sort: "desc",
  },
};

export const DEFAULT_STATUS_FILTERS = [
  "Complete",
  "Rejected",
  "Hold for Cancel",
  "Rejected - Complete",
];

export const STATUS_OPTIONS = [
  "Complete",
  "Rejected",
  "Hold for Cancel",
  "Rejected - Complete",
  "Anomaly Rejected",
  "Error",
];

export const STATUS_CLASS_MAP = {
  "Rejected": "rejected_status",
  "Rejected - Complete": "rejected_status",
  "Anomaly Rejected": "rejected_status",
  "Error": "rejected_status",
  "Complete": "status",
  "Hold for Cancel": "pending_status",
};

// Dashboard lifecycle status shown in the "Processing Status" column (distinct from the Zuora Status).
export const PROCESSING_STATUS_CLASS_MAP = {
  "Pending Approval": "pending_status",
  "Pending L2 Approval": "pending_status",
  "Sync Pending": "sync_pending_status",
  // "In Progress" = pushed to Zuora, workflow still running. Kept visually "in-progress"
  // (not green/done) so business does not mistake it for finished. Formerly "Sync Completed".
  "In Progress": "sync_pending_status",
  "Sync Failed": "rejected_status",
  "Completed": "status",
};

// Post-approval processing states. Rows in these states stay in the Pending Actions tab
// (visible to the whole queue) but are read-only — no approve/reject is possible on them.
export const NON_ACTIONABLE_PROCESSING_STATUSES = [
  "Sync Pending",
  "In Progress",
  "Sync Failed",
  "Completed",
];
